import { Product } from '@shared/components/ui/Product';
import React from 'react';
import { Badge, Checkbox } from '../../../../shared/components/ui';

interface OrderLine {
  id: number;
  qty: number;
  product: {
    id: number;
    name: string;
    code: string;
    image_hash: string;
  };
}

interface OrderDetail {
  id: number;
  ref: string;
  shop: {
    name: string;
    image_hash: string;
  };
  lines: OrderLine[];
}

interface OrderDetailsAccordionProps {
  orders: OrderDetail[];
  isLoading: boolean;
  error: boolean;
  isFetchingNextPage: boolean;
  lastElementRef?: (node: HTMLDivElement | null) => void;
  expandedOrders: Set<number>;
  onToggleExpansion: (orderId: number) => void;

  // Props opcionales para funcionalidad de selección
  enableSelection?: boolean;
  selectedOrderIds?: Set<number>;
  onToggleSelection?: (orderId: number) => void;

  // Props opcionales para personalización
  maxHeight?: string;
  showBorder?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;
}

const OrderDetailsAccordion: React.FC<OrderDetailsAccordionProps> = ({
  orders,
  isLoading,
  error,
  isFetchingNextPage,
  lastElementRef,
  expandedOrders,
  onToggleExpansion,
  enableSelection = false,
  selectedOrderIds,
  onToggleSelection,
  maxHeight = 'max-h-96',
  showBorder = true,
  emptyMessage = 'No hay pedidos para mostrar',
  loadingMessage = 'Cargando detalles...',
}) => {
  // Estados de carga inicial
  if (isLoading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        <span className="ml-2 text-sm text-muted-foreground">{loadingMessage}</span>
      </div>
    );
  }

  // Estado de error
  if (error) {
    return <div className="text-center py-4 text-destructive text-sm">Error al cargar los detalles de los pedidos</div>;
  }

  // Estado vacío
  if (!orders || orders.length === 0) {
    return <div className="text-center py-8 text-muted-foreground text-sm">{emptyMessage}</div>;
  }

  return (
    <div className={`${maxHeight} overflow-y-auto ${showBorder ? 'border rounded-lg' : ''}`}>
      {orders.map((order, index) => (
        <div
          key={order.id}
          className={`border-b border-border last:border-b-0 ${
            enableSelection && selectedOrderIds?.has(order.id) ? 'bg-background' : enableSelection ? 'bg-muted/30' : ''
          }`}
          ref={index === orders.length - 1 ? lastElementRef : null}
        >
          {/* Header del acordeón */}
          <div className="flex items-center">
            {/* Checkbox (solo si está habilitada la selección) */}
            {enableSelection && (
              <div className="px-3 py-3">
                <Checkbox
                  checked={selectedOrderIds?.has(order.id) || false}
                  onCheckedChange={() => onToggleSelection?.(order.id)}
                />
              </div>
            )}

            {/* Botón del acordeón */}
            <button
              onClick={() => onToggleExpansion(order.id)}
              className={`${
                enableSelection ? 'flex-1 px-1' : 'w-full px-4'
              } py-3 flex items-center justify-between hover:bg-accent/50 transition-colors ${
                !enableSelection ? 'rounded-t-lg' : ''
              }`}
            >
              <div className="flex flex-col">
                <span className="text-foreground text-sm">{order.ref}</span>
                <div className="flex items-center gap-2 mt-1">
                  <img src={order.shop.image_hash} alt={order.shop.name} className="w-4 h-4 rounded object-cover" />
                  <span className="text-xs text-muted-foreground">{order.shop.name}</span>
                </div>
              </div>
              <div className={`text-sm ${enableSelection ? 'pr-3' : ''}`}>
                {expandedOrders.has(order.id) ? '−' : '+'}
              </div>
            </button>
          </div>

          {/* Contenido del acordeón */}
          {expandedOrders.has(order.id) && (
            <div className={`px-4 pb-3 border-t border-border ${enableSelection ? 'ml-4' : ''}`}>
              <div className="space-y-3 mt-3">
                {/* Líneas de productos */}
                <div>
                  <div className="space-y-2">
                    {order.lines.map((line) => (
                      <div key={line.id} className="flex items-center gap-3 p-2 border-b border-border last:border-b-0">
                        <Product
                          id={line.product.id}
                          src={line.product.image_hash}
                          name={line.product.name}
                          code={line.product.code}
                          showImageModal={false}
                          enableLineClamp={false}
                          className="flex-1"
                        />
                        <div className="text-right text-sm">
                          <Badge variant="outline" className="font-medium">
                            {line.qty}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Indicador de carga al final */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          <span className="ml-2 text-sm text-muted-foreground">Cargando más pedidos...</span>
        </div>
      )}
    </div>
  );
};

export { OrderDetailsAccordion };
export type { OrderDetail, OrderDetailsAccordionProps, OrderLine };
